import {
  IconAlertCircle,
  IconAlignCenter,
  IconAlignLeft,
  IconAlignRight,
  IconArrowBackUp,
  IconArrowDown,
  IconArrowForwardUp,
  IconArrowLeft,
  IconArrowsDiagonal,
  IconArrowsHorizontal,
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconArrowsVertical,
  IconArrowUp,
  IconArticle,
  IconAspectRatio,
  IconBan,
  IconBell,
  IconBold,
  IconBolt,
  IconBoxMultiple,
  IconBrandAlipay,
  IconBrandDingtalk,
  IconBrandGoogle,
  IconBuildingStore,
  IconBulb,
  IconBulbFilled,
  IconCalendar,
  IconCamera,
  IconCaretDownFilled,
  IconCaretRightFilled,
  IconCaretUpFilled,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconCircleCheck,
  IconCircleCheckFilled,
  IconCircleMinus,
  IconCircleXFilled,
  IconClock,
  IconCloud,
  IconCloudUpload,
  IconCode,
  IconCompass,
  IconCopy,
  IconCrop,
  IconCrosshair,
  IconCrown,
  IconCrownFilled,
  IconDeviceDesktop,
  IconDeviceFloppy,
  IconDeviceMobile,
  IconDeviceTablet,
  IconDots,
  IconDownload,
  IconEdit,
  IconEraser,
  IconExclamationCircleFilled,
  IconEye,
  IconEyeOff,
  IconFile,
  IconFileText,
  IconFlame,
  IconFlipHorizontal,
  IconFlipVertical,
  IconFilter,
  IconFolderOpen,
  IconFolderPlus,
  IconGift,
  IconGitFork,
  IconGrid3x3,
  IconGripVertical,
  IconH1,
  IconH2,
  IconH3,
  IconHeart,
  IconHeartFilled,
  IconHelp,
  IconHistory,
  IconId,
  IconInfoCircle,
  IconInfoCircleFilled,
  IconItalic,
  IconKey,
  IconLayoutAlignBottom,
  IconLayoutAlignMiddle,
  IconLayoutAlignTop,
  IconLayoutGrid,
  IconLink,
  IconList,
  IconListNumbers,
  IconLoader2,
  IconLock,
  IconLockOpen,
  IconLogin,
  IconLogout,
  IconMail,
  IconMap,
  IconMapPin,
  IconMaximize,
  IconMenu2,
  IconMessageCircle,
  IconMinus,
  IconMoon,
  IconMovie,
  IconPaint,
  IconPencil,
  IconPhoto,
  IconPilcrow,
  IconPlayerPause,
  IconPlayerPauseFilled,
  IconPlayerPlay,
  IconPlayerPlayFilled,
  IconPlayerSkipBack,
  IconPlayerSkipForward,
  IconPlug,
  IconPlus,
  IconRefresh,
  IconResize,
  IconRobot,
  IconScissors,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShieldCheck,
  IconShirt,
  IconSitemap,
  IconSparkles,
  IconSquare,
  IconSquareCheck,
  IconStack,
  IconStar,
  IconStarFilled,
  IconStrikethrough,
  IconSun,
  IconTags,
  IconTrash,
  IconUnderline,
  IconUpload,
  IconUser,
  IconUserMinus,
  IconUserPlus,
  IconUsers,
  IconVideo,
  IconVideoPlus,
  IconVolume,
  IconVolumeOff,
  IconWallet,
  IconWand,
  IconWaveSine,
  IconWorld,
  IconX,
  IconZoomIn,
  type TablerIcon,
} from '@tabler/icons-react';
import type { CSSProperties, MouseEvent, ReactNode, Ref } from 'react';
import { forwardRef } from 'react';

interface AntCompatIconProps {
  className?: string;
  style?: CSSProperties;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  spin?: boolean;
  rotate?: number;
  twoToneColor?: string;
  color?: string;
  [key: string]: unknown;
}

function makeIcon(TIcon: TablerIcon) {
  return forwardRef<SVGSVGElement, AntCompatIconProps>(function AntCompatIcon(
    {
      className,
      style,
      onClick,
      spin,
      rotate,
      twoToneColor,
      color,
      ...rest
    }: AntCompatIconProps,
    ref: Ref<SVGSVGElement>,
  ) {
    let size: number | string = '1em';
    const finalStyle: CSSProperties = { ...(style ?? {}) };
    if (style?.fontSize !== undefined) {
      const fs = style.fontSize;
      size = typeof fs === 'number' ? fs : (fs as string);
      delete finalStyle.fontSize;
    }
    if (rotate) {
      finalStyle.transform =
        `${finalStyle.transform ?? ''} rotate(${rotate}deg)`.trim();
    }
    const finalClass = [className, 'tabler-icon', spin ? 'tabler-spin' : null]
      .filter(Boolean)
      .join(' ');
    void twoToneColor;
    return (
      <TIcon
        ref={ref}
        size={size}
        stroke={2}
        color={color ?? 'currentColor'}
        className={finalClass}
        style={finalStyle}
        onClick={onClick as unknown as React.MouseEventHandler<SVGSVGElement>}
        {...(rest as Record<string, unknown>)}
      />
    );
  });
}

export type AntCompatIcon = ReactNode;
export const AimOutlined = makeIcon(IconCrosshair);
export const AlignCenterOutlined = makeIcon(IconAlignCenter);
export const AlignLeftOutlined = makeIcon(IconAlignLeft);
export const AlignRightOutlined = makeIcon(IconAlignRight);
export const BoldOutlined = makeIcon(IconBold);
export const H1Outlined = makeIcon(IconH1);
export const H2Outlined = makeIcon(IconH2);
export const H3Outlined = makeIcon(IconH3);
export const ParagraphOutlined = makeIcon(IconPilcrow);
export const UnorderedListOutlined = makeIcon(IconList);
export const ItalicOutlined = makeIcon(IconItalic);
export const UnderlineOutlined = makeIcon(IconUnderline);
export const StrikethroughOutlined = makeIcon(IconStrikethrough);
export const SwapOutlined = makeIcon(IconFlipHorizontal);
export const FlipHorizontalOutlined = makeIcon(IconFlipHorizontal);
export const FlipVerticalOutlined = makeIcon(IconFlipVertical);
export const AlipayOutlined = makeIcon(IconBrandAlipay);
export const ApartmentOutlined = makeIcon(IconSitemap);
export const ApiOutlined = makeIcon(IconPlug);
export const AppstoreOutlined = makeIcon(IconLayoutGrid);
export const AspectRatioOutlined = makeIcon(IconAspectRatio);
export const BoxMultipleOutlined = makeIcon(IconBoxMultiple);
export const ArrowDownOutlined = makeIcon(IconArrowDown);
export const ArrowLeftOutlined = makeIcon(IconArrowLeft);
export const ArrowUpOutlined = makeIcon(IconArrowUp);
export const ArticleOutlined = makeIcon(IconArticle);
export const AudioMutedOutlined = makeIcon(IconVolumeOff);
export const BellOutlined = makeIcon(IconBell);
export const BgColorsOutlined = makeIcon(IconPaint);
export const BlockOutlined = makeIcon(IconStack);
export const BorderOutlined = makeIcon(IconSquare);
export const BulbFilled = makeIcon(IconBulbFilled);
export const BulbOutlined = makeIcon(IconBulb);
export const CalendarOutlined = makeIcon(IconCalendar);
export const CameraOutlined = makeIcon(IconCamera);
export const CaretDownOutlined = makeIcon(IconCaretDownFilled);
export const CaretRightOutlined = makeIcon(IconCaretRightFilled);
export const CaretUpOutlined = makeIcon(IconCaretUpFilled);
export const CheckCircleFilled = makeIcon(IconCircleCheckFilled);
export const CheckCircleOutlined = makeIcon(IconCircleCheck);
export const CheckOutlined = makeIcon(IconCheck);
export const CheckSquareOutlined = makeIcon(IconSquareCheck);
export const ClockCircleOutlined = makeIcon(IconClock);
export const CloseCircleFilled = makeIcon(IconCircleXFilled);
export const CloseOutlined = makeIcon(IconX);
export const CloudOutlined = makeIcon(IconCloud);
export const CloudUploadOutlined = makeIcon(IconCloudUpload);
export const CodeOutlined = makeIcon(IconCode);
export const ColumnHeightOutlined = makeIcon(IconArrowsVertical);
export const ColumnWidthOutlined = makeIcon(IconArrowsHorizontal);
export const CompassOutlined = makeIcon(IconCompass);
export const CompressOutlined = makeIcon(IconArrowsMinimize);
export const CopyOutlined = makeIcon(IconCopy);
export const CropOutlined = makeIcon(IconCrop);
export const CrownFilled = makeIcon(IconCrownFilled);
export const CrownOutlined = makeIcon(IconCrown);
export const DeleteOutlined = makeIcon(IconTrash);
export const DesktopOutlined = makeIcon(IconDeviceDesktop);
export const DingdingOutlined = makeIcon(IconBrandDingtalk);
export const DownloadOutlined = makeIcon(IconDownload);
export const DownOutlined = makeIcon(IconChevronDown);
export const DragOutlined = makeIcon(IconGripVertical);
export const EditOutlined = makeIcon(IconPencil);
export const EnvironmentOutlined = makeIcon(IconMapPin);
export const ExclamationCircleFilled = makeIcon(IconExclamationCircleFilled);
export const ExclamationCircleOutlined = makeIcon(IconAlertCircle);
export const ExpandAltOutlined = makeIcon(IconArrowsDiagonal);
export const ExpandOutlined = makeIcon(IconArrowsMaximize);
export const ExportOutlined = makeIcon(IconLogout);
export const EyeInvisibleOutlined = makeIcon(IconEyeOff);
export const EyeOutlined = makeIcon(IconEye);
export const FileImageOutlined = makeIcon(IconPhoto);
export const FileOutlined = makeIcon(IconFile);
export const FileSyncOutlined = makeIcon(IconRefresh);
export const FileTextOutlined = makeIcon(IconFileText);
export const FireOutlined = makeIcon(IconFlame);
export const FolderAddOutlined = makeIcon(IconFolderPlus);
export const FolderOpenOutlined = makeIcon(IconFolderOpen);
export const ForkOutlined = makeIcon(IconGitFork);
export const FormOutlined = makeIcon(IconEdit);
export const FullscreenOutlined = makeIcon(IconMaximize);
export const GiftOutlined = makeIcon(IconGift);
export const GlobalOutlined = makeIcon(IconWorld);
export const GoogleOutlined = makeIcon(IconBrandGoogle);
export const GridSliceOutlined = makeIcon(IconGrid3x3);
export const HeartFilled = makeIcon(IconHeartFilled);
export const HeartOutlined = makeIcon(IconHeart);
export const HistoryOutlined = makeIcon(IconHistory);
export const IdcardOutlined = makeIcon(IconId);
export const InfoCircleFilled = makeIcon(IconInfoCircleFilled);
export const InfoCircleOutlined = makeIcon(IconInfoCircle);
export const ImportOutlined = makeIcon(IconLogin);
export const KeyOutlined = makeIcon(IconKey);
export const LinkOutlined = makeIcon(IconLink);
const LoadingOutlinedRaw = makeIcon(IconLoader2);
export const LoadingOutlined = forwardRef<SVGSVGElement, AntCompatIconProps>(
  function LoadingOutlined({ spin = true, ...rest }, ref) {
    return <LoadingOutlinedRaw ref={ref} spin={spin} {...rest} />;
  },
);
export const LockOutlined = makeIcon(IconLock);
export const LogoutOutlined = makeIcon(IconLogout);
export const MailOutlined = makeIcon(IconMail);
export const MapOutlined = makeIcon(IconMap);
export const MenuUnfoldOutlined = makeIcon(IconMenu2);
export const MessageOutlined = makeIcon(IconMessageCircle);
export const MinusCircleOutlined = makeIcon(IconCircleMinus);
export const MobileOutlined = makeIcon(IconDeviceMobile);
export const MonitorOutlined = makeIcon(IconDeviceDesktop);
export const MoreOutlined = makeIcon(IconDots);
export const MovieOutlined = makeIcon(IconMovie);
export const OrderedListOutlined = makeIcon(IconListNumbers);
export const PauseCircleFilled = makeIcon(IconPlayerPauseFilled);
export const PauseOutlined = makeIcon(IconPlayerPause);
export const PictureOutlined = makeIcon(IconPhoto);
export const PlayCircleFilled = makeIcon(IconPlayerPlayFilled);
export const PlayCircleOutlined = makeIcon(IconPlayerPlay);
export const PlaySquareOutlined = makeIcon(IconPlayerPlay);
export const MinusOutlined = makeIcon(IconMinus);
export const MoonOutlined = makeIcon(IconMoon);
export const SunOutlined = makeIcon(IconSun);
export const PlusOutlined = makeIcon(IconPlus);
export const QuestionCircleOutlined = makeIcon(IconHelp);
export const RedoOutlined = makeIcon(IconArrowForwardUp);
export const ReloadOutlined = makeIcon(IconRefresh);
export const RightOutlined = makeIcon(IconChevronRight);
export const RobotOutlined = makeIcon(IconRobot);
export const SafetyOutlined = makeIcon(IconShieldCheck);
export const SaveOutlined = makeIcon(IconDeviceFloppy);
export const ScissorOutlined = makeIcon(IconScissors);
export const SearchOutlined = makeIcon(IconSearch);
export const SendOutlined = makeIcon(IconSend);
export const SettingOutlined = makeIcon(IconSettings);
export const ShareAltOutlined = makeIcon(IconShare);
export const SkinOutlined = makeIcon(IconShirt);
export const SoundOutlined = makeIcon(IconVolume);
export const AudioWaveOutlined = makeIcon(IconWaveSine);
export const SparkleOutlined = makeIcon(IconSparkles);
export const StarFilled = makeIcon(IconStarFilled);
export const StarOutlined = makeIcon(IconStar);
export const StepBackwardOutlined = makeIcon(IconPlayerSkipBack);
export const StepForwardOutlined = makeIcon(IconPlayerSkipForward);
export const StopOutlined = makeIcon(IconBan);
export const TabletOutlined = makeIcon(IconDeviceTablet);
export const TagsOutlined = makeIcon(IconTags);
export const TaobaoOutlined = makeIcon(IconBuildingStore);
export const TeamOutlined = makeIcon(IconUsers);
export const ThunderboltOutlined = makeIcon(IconBolt);
export const UndoOutlined = makeIcon(IconArrowBackUp);
export const UnlockOutlined = makeIcon(IconLockOpen);
export const UploadOutlined = makeIcon(IconUpload);
export const UpOutlined = makeIcon(IconChevronUp);
export const UserAddOutlined = makeIcon(IconUserPlus);
export const UserDeleteOutlined = makeIcon(IconUserMinus);
export const UserOutlined = makeIcon(IconUser);
export const VerticalAlignBottomOutlined = makeIcon(IconLayoutAlignBottom);
export const VerticalAlignMiddleOutlined = makeIcon(IconLayoutAlignMiddle);
export const VerticalAlignTopOutlined = makeIcon(IconLayoutAlignTop);
export const VideoCameraAddOutlined = makeIcon(IconVideoPlus);
export const VideoCameraOutlined = makeIcon(IconVideo);
export const WalletOutlined = makeIcon(IconWallet);
export const WandOutlined = makeIcon(IconWand);
export const ResizeOutlined = makeIcon(IconResize);
export const EraserOutlined = makeIcon(IconEraser);
export const ZoomInOutlined = makeIcon(IconZoomIn);

export const FilterOutlined = makeIcon(IconFilter);
